import React, { createContext, useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldAlert } from 'lucide-react';

interface ContentProtectionContextType {
  showNotification: () => void;
}

const ContentProtectionContext = createContext<ContentProtectionContextType | undefined>(undefined);

export const ContentProtectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  let timeout: NodeJS.Timeout;

  const showNotification = () => {
    setIsVisible(true);
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      setIsVisible(false);
    }, 3000);
  };

  const handleClose = () => {
    setIsVisible(false);
    clearTimeout(timeout);
  };

  React.useEffect(() => {
    const preventDefaultBehavior = (e: Event) => {
      e.preventDefault();
      showNotification();
    };

    const preventCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      showNotification();
    };

    const preventKeyboardShortcuts = (e: KeyboardEvent) => {
      if (
        (e.ctrlKey || e.metaKey) && 
        (e.key === 'c' || e.key === 'C' || e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U')
      ) {
        e.preventDefault();
        showNotification();
      }
    };

    document.addEventListener('contextmenu', preventDefaultBehavior);
    document.addEventListener('copy', preventCopy);
    document.addEventListener('keydown', preventKeyboardShortcuts);
    document.addEventListener('dragstart', preventDefaultBehavior);
    document.addEventListener('selectstart', preventDefaultBehavior);

    return () => {
      document.removeEventListener('contextmenu', preventDefaultBehavior);
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('keydown', preventKeyboardShortcuts);
      document.removeEventListener('dragstart', preventDefaultBehavior);
      document.removeEventListener('selectstart', preventDefaultBehavior);
    };
  }, []);

  return (
    <ContentProtectionContext.Provider value={{ showNotification }}>
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 350,
              damping: 25
            }}
            className="fixed bottom-0 left-0 right-0 z-50"
          >
            <div className="container mx-auto px-4 py-4">
              <div className="bg-white shadow-lg rounded-xl p-4 flex items-center justify-between max-w-2xl mx-auto">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShieldAlert className="w-5 h-5 text-success" />
                  </div>
                  <p className="text-gray-600 text-sm">
                    Die Inhalte dieser Website sind urheberrechtlich geschützt und dürfen ohne ausdrückliche Genehmigung nicht verwendet, vervielfältigt oder verbreitet werden.
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-500 transition-colors ml-4"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ContentProtectionContext.Provider>
  );
};

export const useContentProtection = () => {
  const context = useContext(ContentProtectionContext);
  if (context === undefined) {
    throw new Error('useContentProtection must be used within a ContentProtectionProvider');
  }
  return context;
};